export const EXPERIENCE_COOKIE_NAME = 'NODAFI_WEB_EXPERIENCE'

export type WebExperience = 'legacy' | 'orchid'

/**
 * Long enough that a switch survives a browser restart, short enough that a
 * preference nobody revisits expires instead of outliving the rollout.
 */
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60

const EXPERIENCES: readonly string[] = ['legacy', 'orchid']

/**
 * The cookie CloudFront reads to pick an origin. Host-only — no Domain
 * attribute — because a user can belong to several tenants and be on a
 * different experience in each.
 */
export const buildExperienceCookie = (experience: WebExperience, isSecure: boolean): string => {
  const attributes = [
    `${EXPERIENCE_COOKIE_NAME}=${experience}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ]
  // A Secure cookie is dropped on http://localhost, which would leave the
  // toggle silently inert in local development.
  if (isSecure) attributes.push('Secure')
  return attributes.join('; ')
}

export const readExperienceCookie = (cookie: string): WebExperience | null => {
  for (const entry of cookie.split(';')) {
    const separator = entry.indexOf('=')
    if (separator === -1) continue
    // Compare the NAME, not a prefix: `OTHER_NODAFI_WEB_EXPERIENCE` shares our
    // suffix and would otherwise shadow the real cookie sitting behind it.
    if (entry.slice(0, separator).trim() !== EXPERIENCE_COOKIE_NAME) continue
    const value = entry.slice(separator + 1).trim()
    if (EXPERIENCES.includes(value)) return value as WebExperience
  }
  return null
}


/**
 * What to write so this device matches the reader's actual preference, or
 * `null` when it already does.
 *
 * The cookie is a CACHE, not a second source of truth. It exists only because
 * CloudFront picks an origin before any app code runs, with the hostname, the
 * path and the headers to go on — there is no other channel by which a per-user
 * fact reaches that decision.
 *
 * Treating it as authoritative is what made the saved preference inert: a
 * device holding a stale cookie never updated, so enabling the switch on one
 * device changed nothing on any other. Here a disagreement means the cache is
 * STALE, and stale caches get corrected rather than obeyed.
 *
 * An absent cookie is compared as `legacy`, because that is what the edge does
 * with it. That is what keeps this from writing a pointless `legacy` cookie
 * onto a device already being served legacy — a write that would change no
 * routing while inventing a device-local choice the reader never made.
 *
 * `null` on either preference means NOT KNOWN, never `false`. A failed lookup
 * must not read as an opt-out: that would turn every network blip into a silent
 * rollback out of Orchid.
 */
export const experienceToReconcile = ({
  cookie,
  isOrchidChosenByUser,
  isOrchidDefaultForOrg,
}: {
  cookie: string
  isOrchidChosenByUser: boolean | null
  isOrchidDefaultForOrg: boolean | null
}): WebExperience | null => {
  // An explicit reader choice outranks the organization default; neither known
  // means there is nothing to reconcile TO, so the device is left as it is.
  const isOrchidPreferred = isOrchidChosenByUser ?? isOrchidDefaultForOrg
  if (isOrchidPreferred === null) return null

  const wanted: WebExperience = isOrchidPreferred ? 'orchid' : 'legacy'
  const current = readExperienceCookie(cookie) ?? 'legacy'
  return current === wanted ? null : wanted
}

/**
 * Write the saved preference onto this device if it has not chosen one.
 *
 * Separated from the hook that calls it so the side effect is testable: the
 * decision alone is `experienceToReconcile`, and a decision nothing acts on is the
 * shape where a fix looks shipped and changes nothing.
 *
 * Reloads only when it actually wrote a cookie. The edge reads the cookie at
 * request time, so the document already in the browser was served by whichever
 * app the ABSENT cookie selected; without the reload the reader stays there
 * until they navigate, which is the "it did nothing" report.
 *
 * @returns the experience written, or null when it left the device alone.
 */
export const reconcileExperienceCookie = (
  preferences: { isOrchidChosenByUser: boolean | null; isOrchidDefaultForOrg: boolean | null },
  browser: {
    getCookie: () => string
    setCookie: (value: string) => void
    isSecure: boolean
    reload: () => void
  },
  report?: (event: { from: WebExperience; to: WebExperience }) => void,
): WebExperience | null => {
  const cookie = browser.getCookie()
  const experience = experienceToReconcile({ cookie, ...preferences })
  if (experience === null) return null

  // Reported because a correction IS the defect: the reader was in the wrong app
  // until this load moved them, and nothing else can see that. A CloudFront log
  // records what a request carried, and a stranded reader carried no experience
  // cookie — the same as someone who never opted in. Only here are both the
  // running app and the account's preference known at once.
  //
  // Optional, and never awaited: telemetry that can break a page is worse than
  // no telemetry.
  report?.({ from: readExperienceCookie(cookie) ?? 'legacy', to: experience })
  browser.setCookie(buildExperienceCookie(experience, browser.isSecure))
  browser.reload()
  return experience
}

